export type NavigationIntent = 'replace-current-stream' | 'open-new-stream-right';

export interface NavigationIntentDetail {
  intent: NavigationIntent;
  source: 'click' | 'ctrl-click' | 'long-press';
}

export interface NavigationIntentOptions {
  onIntent: (detail: NavigationIntentDetail) => void;
  longPressMs?: number;
  movementTolerance?: number;
}

const DEFAULT_LONG_PRESS_MS = 400;
const DEFAULT_MOVEMENT_TOLERANCE = 10;
const ARMING_CLASS = 'navigation-intent-target--arming';

export function navigationIntent(node: HTMLElement, options: NavigationIntentOptions) {
  let currentOptions = options;
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  let activePointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let suppressNextClick = false;

  function dispatch(detail: NavigationIntentDetail): void {
    currentOptions.onIntent(detail);
  }

  function clearPressState(): void {
    if (pressTimer !== null) {
      clearTimeout(pressTimer);
      pressTimer = null;
    }

    activePointerId = null;
    node.classList.remove(ARMING_CLASS);
  }

  function handlePointerDown(event: PointerEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
      return;
    }

    clearPressState();
    activePointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    node.classList.add(ARMING_CLASS);
    try {
      node.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignore browsers that do not allow pointer capture on this target.
    }

    pressTimer = setTimeout(() => {
      pressTimer = null;
      suppressNextClick = true;
      node.classList.remove(ARMING_CLASS);
      dispatch({ intent: 'open-new-stream-right', source: 'long-press' });
    }, currentOptions.longPressMs ?? DEFAULT_LONG_PRESS_MS);
  }

  function handlePointerMove(event: PointerEvent): void {
    if (activePointerId !== event.pointerId || pressTimer === null) {
      return;
    }

    const movementTolerance = currentOptions.movementTolerance ?? DEFAULT_MOVEMENT_TOLERANCE;
    const distance = Math.hypot(event.clientX - startX, event.clientY - startY);
    if (distance > movementTolerance) {
      clearPressState();
    }
  }

  function handlePointerEnd(event: PointerEvent): void {
    if (activePointerId !== event.pointerId) {
      return;
    }

    try {
      if (node.hasPointerCapture?.(event.pointerId)) {
        node.releasePointerCapture?.(event.pointerId);
      }
    } catch {
      // Ignore browsers that do not allow pointer capture on this target.
    }

    clearPressState();
  }

  function handleClick(event: MouseEvent): void {
    if (suppressNextClick) {
      suppressNextClick = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      dispatch({
        intent: 'open-new-stream-right',
        source: event.metaKey ? 'ctrl-click' : 'ctrl-click',
      });
      return;
    }

    dispatch({ intent: 'replace-current-stream', source: 'click' });
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (
      (event.ctrlKey || event.metaKey) &&
      !event.altKey &&
      !event.shiftKey &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      dispatch({ intent: 'open-new-stream-right', source: 'ctrl-click' });
    }
  }

  function handleContextMenu(event: MouseEvent): void {
    if (pressTimer !== null || suppressNextClick) {
      event.preventDefault();
    }
  }

  node.addEventListener('pointerdown', handlePointerDown);
  node.addEventListener('pointermove', handlePointerMove);
  node.addEventListener('pointerup', handlePointerEnd);
  node.addEventListener('pointercancel', handlePointerEnd);
  node.addEventListener('pointerleave', handlePointerEnd);
  node.addEventListener('click', handleClick);
  node.addEventListener('contextmenu', handleContextMenu);
  node.addEventListener('keydown', handleKeyDown);

  return {
    update(nextOptions: NavigationIntentOptions) {
      currentOptions = nextOptions;
    },
    destroy() {
      clearPressState();
      node.removeEventListener('pointerdown', handlePointerDown);
      node.removeEventListener('pointermove', handlePointerMove);
      node.removeEventListener('pointerup', handlePointerEnd);
      node.removeEventListener('pointercancel', handlePointerEnd);
      node.removeEventListener('pointerleave', handlePointerEnd);
      node.removeEventListener('click', handleClick);
      node.removeEventListener('contextmenu', handleContextMenu);
      node.removeEventListener('keydown', handleKeyDown);
    },
  };
}
